"use client"

import { useState, useRef, useEffect } from "react"
import { useTheme } from "../contexts/ThemeContext"

// Add video detection function at the top
const isVideo = (url) => {
  if (!url) return false;
  return url.includes('.mp4') || url.includes('.webm') || url.includes('.mov') || 
         url.includes('video') || url.includes('.avi') || url.includes('.mkv') ||
         url.toLowerCase().includes('video');
};

// Utility: check if an image is available for download
export function isImageAvailable(image, brokenImages) {
  return (
    image?.image_url &&
    image?.id !== undefined &&
    !brokenImages?.[image.id] &&
    image.image_url !== ''
  );
}

function ImageViewer({ image, onClose, brokenImages }) {
  const [isLoading, setIsLoading] = useState(true)
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [copied, setCopied] = useState(false)
  const canvasRef = useRef(null)
  const imageRef = useRef(null)
  const { theme } = useTheme();

  useEffect(() => {
    if (image && image.image_url) {
      // Skip canvas loading for videos
      if (isVideo(image.image_url)) {
        setIsLoading(false)
        return
      }
      
      const img = new window.Image()
      img.crossOrigin = "anonymous"
      img.onload = () => {
        const canvas = canvasRef.current
        const ctx = canvas.getContext("2d")
        canvas.width = img.naturalWidth
        canvas.height = img.naturalHeight
        ctx.drawImage(img, 0, 0)
        setIsLoading(false)
      }
      img.onerror = () => {
        setIsLoading(false)
        console.error("Failed to load image")
      }
      img.src = image.image_url
      imageRef.current = img
    }
  }, [image])

  const getFileExtension = (url) => {
    const match = url.match(/\.([a-zA-Z0-9]+)(?:\?|$)/);
    return match ? match[1] : 'png';
  };

  const handleDownload = async () => {
    if (!canvasRef.current || !image?.image_url) return;
    setDownloadLoading(true);
    try {
      const canvas = canvasRef.current;
      const ext = getFileExtension(image.image_url);
      const mimeType = ext === 'jpg' ? 'image/jpeg' : ext === 'jpeg' ? 'image/jpeg' : ext === 'webp' ? 'image/webp' : ext === 'png' ? 'image/png' : 'image/png';
      const link = document.createElement("a");
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.download = `ai-generated-image-${Date.now()}.${ext}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        setDownloadLoading(false);
      }, mimeType);
    } catch (error) {
      console.error("Download failed:", error);
      setDownloadLoading(false);
    }
  }

  const handleDownloadOriginal = async () => {
    if (!image?.image_url) return;
    setDownloadLoading(true);
    try {
      const isVideoFile = isVideo(image.image_url);
      const ext = isVideoFile ? 'mp4' : getFileExtension(image.image_url);
      
      const response = await fetch(image.image_url);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `ai-generated-${isVideoFile ? 'video' : 'image'}-${Date.now()}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setDownloadLoading(false);
    }
  }

  const handleCopyPrompt = () => {
    if (image?.prompt) {
      navigator.clipboard.writeText(image.prompt)
      setCopied(true)
      setTimeout(() => setCopied(false), 1200)
    }
  }

  // Use the shared utility for download availability
  const isAvailable = isImageAvailable(image, brokenImages);

  if (!image) return null

  return (
    <div className={theme === "dark" ? "fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/80 backdrop-blur-xl" : "fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-gradient-to-br from-indigo-900/80 via-purple-900/70 to-pink-900/80 backdrop-blur-xl"}>
      <div className={theme === "dark" ? "relative bg-black rounded-2xl max-w-2xl w-full max-h-[95vh] shadow-2xl border border-gray-700 animate-fadeIn flex flex-col overflow-y-auto text-white" : "relative bg-white/90 dark:bg-gray-900/90 rounded-2xl max-w-2xl w-full max-h-[95vh] shadow-2xl border border-white/20 dark:border-gray-700/40 animate-fadeIn flex flex-col overflow-y-auto"}>
        {/* Animated Gradient Border */}
        <div className={theme === "dark" ? "absolute -inset-1 rounded-2xl pointer-events-none z-0 animate-gradientBorder bg-gradient-to-tr from-gray-600 via-gray-500 to-gray-400 opacity-50 blur-[2px]" : "absolute -inset-1 rounded-2xl pointer-events-none z-0 animate-gradientBorder bg-gradient-to-tr from-pink-400 via-indigo-400 to-purple-400 opacity-80 blur-[2px]"} />
        {/* Header */}
        <div className={theme === "dark" ? "relative z-10 flex items-center justify-between p-4 border-b border-gray-700 bg-black rounded-t-2xl" : "relative z-10 flex items-center justify-between p-4 border-b border-white/20 dark:border-gray-700/40 bg-white/80 dark:bg-gray-900/80 rounded-t-2xl"}>
          <div>
            <h3 className={theme === "dark" ? "text-lg font-semibold text-white tracking-tight drop-shadow" : "text-lg font-semibold text-gray-900 dark:text-white tracking-tight drop-shadow"}>
              {isVideo(image.image_url) ? "Generated Video" : "Generated Image"}
            </h3>
            <p className={theme === "dark" ? "text-xs text-gray-300 mt-1" : "text-xs text-gray-600 dark:text-gray-300 mt-1"}>{new Date(image.created_at).toLocaleString()}</p>
          </div>
          <button
            onClick={onClose}
            className={theme === "dark" ? "group text-gray-400 hover:text-red-400 transition-colors rounded-full p-1 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-red-400 scale-100 hover:scale-110 active:scale-95 duration-150" : "group text-gray-400 hover:text-pink-500 transition-colors rounded-full p-1 hover:bg-white/30 dark:hover:bg-gray-800/40 focus:outline-none focus:ring-2 focus:ring-pink-400 scale-100 hover:scale-110 active:scale-95 duration-150"}
            aria-label="Close"
          >
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Image Display */}
        <div className={theme === "dark" ? "relative z-10 flex flex-col items-center justify-center p-2 sm:p-6 bg-black" : "relative z-10 flex flex-col items-center justify-center p-2 sm:p-6 bg-white/70 dark:bg-gray-900/70"}>
          <div className="w-full flex justify-center items-center mb-4">
            <div className="relative w-full max-w-[90%] flex items-center justify-center">
              {isLoading && !isVideo(image.image_url) && (
                <div className={theme === "dark" ? "absolute inset-0 flex items-center justify-center bg-black/80 rounded-xl z-20" : "absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100/80 via-indigo-100/60 to-pink-100/60 rounded-xl z-20"}>
                  <div className={theme === "dark" ? "animate-spin rounded-full h-14 w-14 border-b-4 border-white" : "animate-spin rounded-full h-14 w-14 border-b-4 border-indigo-500"}></div>
                </div>
              )}
              
              {isVideo(image.image_url) ? (
                <video
                  src={image.image_url}
                  className={theme === "dark" ? "w-full max-h-[60vh] rounded-xl shadow-2xl border-2 border-gray-700 bg-gray-900 transition-all duration-700 opacity-100 scale-100 animate-fadeInImg" : "w-full max-h-[60vh] rounded-xl shadow-2xl border-2 border-white/40 dark:border-gray-700/40 bg-white/10 dark:bg-gray-900/20 transition-all duration-700 opacity-100 scale-100 animate-fadeInImg"}
                  style={{ objectFit: 'contain' }}
                  controls
                  autoPlay
                  loop
                  muted
                  onError={(e) => {
                    console.error("Video failed to load in viewer:", e);
                  }}
                />
              ) : (
                <canvas
                  ref={canvasRef}
                  className={theme === "dark" ? `w-full max-h-[60vh] rounded-xl shadow-2xl border-2 border-gray-700 bg-gray-900 transition-all duration-700 ${isLoading ? 'opacity-0 scale-95' : 'opacity-100 scale-100'} animate-fadeInImg` : `w-full max-h-[60vh] rounded-xl shadow-2xl border-2 border-white/40 dark:border-gray-700/40 bg-white/10 dark:bg-gray-900/20 transition-all duration-700 ${isLoading ? 'opacity-0 scale-95' : 'opacity-100 scale-100'} animate-fadeInImg`}
                  style={{ objectFit: 'contain' }}
                />
              )}
              
              {/* Fallback image if canvas fails */}
              {!isLoading && !isVideo(image.image_url) && (
                <div className="hidden">
                  <img
                    src={image.image_url}
                    alt={image.prompt}
                    ref={imageRef}
                    crossOrigin="anonymous"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Prompt & Actions */}
          <div className="w-full flex flex-col gap-3 items-stretch">
            <div className={theme === "dark" ? "relative z-10 bg-black border border-gray-700 rounded-xl shadow-inner p-3 flex flex-col gap-2" : "relative z-10 bg-white dark:bg-gray-800 rounded-xl shadow-inner border border-white/20 dark:border-gray-700/30 p-3 flex flex-col gap-2"}>
              <div className="mb-1">
                <p className={theme === "dark" ? "text-xs text-gray-300 font-medium mb-1" : "text-xs text-gray-500 dark:text-gray-300 font-medium mb-1"}>Prompt:</p>
                <p className={theme === "dark" ? "text-sm text-gray-100 break-words" : "text-sm text-gray-800 dark:text-gray-100 break-words"} title={image.prompt}>{image.prompt}</p>
              </div>
              <button
                onClick={handleCopyPrompt}
                className="relative flex items-center justify-center px-3 py-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white rounded-md shadow hover:from-pink-500 hover:to-indigo-500 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-pink-400 w-full text-sm"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16h8M8 12h8m-7 8h6a2 2 0 002-2V6a2 2 0 00-2-2H7a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {copied ? 'Copied!' : 'Copy Prompt'}
                {copied && (
                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black/80 text-white text-xs rounded px-2 py-1 shadow animate-fadeIn whitespace-nowrap">Copied!</span>
                )}
              </button>
              <button
                onClick={isVideo(image.image_url) ? handleDownloadOriginal : handleDownload}
                disabled={isLoading || downloadLoading || !isAvailable}
                className="relative flex items-center justify-center px-3 py-2 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white rounded-md shadow hover:from-pink-600 hover:to-indigo-600 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-400 w-full text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {downloadLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Downloading...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {isVideo(image.image_url) ? 'Download Video' : 'Download Canvas'}
                  </>
                )}
              </button>
              {!isVideo(image.image_url) && (
                <button
                  onClick={handleDownloadOriginal}
                  disabled={isLoading || downloadLoading || !isAvailable}
                  className="relative flex items-center justify-center px-3 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-md shadow hover:from-emerald-600 hover:to-green-500 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-400 w-full text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {downloadLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Downloading...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download Original
                    </>
                  )}
                </button>
              )}
              {!isAvailable && (
                <div className={theme === "dark" ? "mt-2 text-xs text-yellow-400 bg-yellow-900/20 border border-yellow-700 rounded p-2 text-center font-medium" : "mt-2 text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded p-2 text-center font-medium"}>
                  This {isVideo(image.image_url) ? 'video' : 'image'} is no longer available for download, but you can still view the prompt.
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Footer Close Button */}
        <div className={theme === "dark" ? "relative z-10 flex justify-center items-center p-4 border-t border-gray-700 bg-black rounded-b-2xl" : "relative z-10 flex justify-center items-center p-4 border-t border-white/20 dark:border-gray-700/40 bg-white/80 dark:bg-gray-900/80 rounded-b-2xl"}>
          <button
            onClick={onClose}
            className={theme === "dark" ? "flex items-center gap-2 px-5 py-2 bg-gray-800 text-white rounded-md shadow hover:bg-gray-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400 text-base font-semibold border border-gray-700" : "flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 text-white rounded-md shadow hover:from-indigo-500 hover:to-pink-500 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-pink-400 text-base font-semibold"}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default ImageViewer